import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  RotateCcw,
  UserPlus,
  LogIn,
  Key,
  CheckSquare,
  ShieldCheck,
  UserCheck,
  AlertTriangle
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import TempPasswordModal from '@/components/TempPasswordModal';


export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [page, setPage] = useState(0);
  const [editUser, setEditUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState(null);
  const [hardDeleteEmail, setHardDeleteEmail] = useState('');
  const [statusModal, setStatusModal] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [impersonateConfirm, setImpersonateConfirm] = useState(null);
  
  // Multi-select state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [tempPasswordModal, setTempPasswordModal] = useState({ open: false, password: '', user: null });
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'coach', password: '' });
  const navigate = useNavigate();
  const limit = 20;

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);
      
      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  const handleUpdateStatus = async (userId, newStatus) => {
    try {
      await api.patch(`/admin/users/${userId}/status`, { status: newStatus });
      toast.success(`User status updated to ${newStatus}`);
      setStatusModal(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User archived');
      setDeleteConfirm(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to archive user');
    }
  };

  const handleHardDelete = async (user) => {
    if (hardDeleteEmail !== user.email) {
      toast.error('Please type the email correctly to confirm deletion');
      return;
    }
    try {
      const response = await api.delete(`/admin/users/${user.user_id}/hard-delete`);
      toast.success(`Test account permanently deleted. Teams: ${response.data.deleted_data?.teams_deleted || 0}, Games: ${response.data.deleted_data?.games_deleted || 0}`);
      setHardDeleteConfirm(null);
      setHardDeleteEmail('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleRestore = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/restore`);
      toast.success('User restored');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to restore user');
    }
  };

  const handleUpdateUser = async () => {
    try {
      await api.patch(`/admin/users/${editUser.user_id}`, {
        name: editUser.name,
        email: editUser.email,
        role: editUser.role
      });
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.name) {
      toast.error('Name and email are required');
      return;
    }
    try {
      const response = await api.post('/admin/users', newUser);
      if (response.data.password) {
        toast.success(`User created! Temporary password: ${response.data.password}`, { duration: 10000 });
      } else {
        toast.success('User created');
      }
      setCreateDialogOpen(false);
      setNewUser({ name: '', email: '', role: 'coach', password: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleImpersonate = async (user) => {
    try {
      await api.post(`/admin/impersonate/${user.user_id}`);
      toast.success(`Now viewing as ${user.name || user.email}`);
      setImpersonateConfirm(null);
      // Navigate to dashboard to see as user
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const handleGenerateTempPassword = async (user) => {
    try {
      const response = await api.post(`/admin/users/${user.user_id}/temp-password`);
      setTempPasswordModal({
        open: true,
        password: response.data.temp_password,
        user: user
      });
      toast.success('Temporary password generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate temporary password');
    }
  };

  const getStatusBadge = (user) => {
    if (user.deleted_at) {
      return <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-700 rounded">Deleted</span>;
    }
    const isAccountInactive = user.status === 'inactive' || user.is_suspended;
    if (isAccountInactive) {
      return <span className="px-2 py-1 text-xs font-bold bg-slate-200 text-slate-600 rounded">Inactive</span>;
    }
    if (user.must_change_password) {
      return <span className="px-2 py-1 text-xs font-bold bg-amber-100 text-amber-700 rounded">Temp Password</span>;
    }
    return <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-700 rounded">Active</span>;
  };

  const getRoleBadge = (role) => {
    const colors = {
      coach: 'bg-blue-100 text-blue-700',
      staff: 'bg-purple-100 text-purple-700',
      player: 'bg-green-100 text-green-700',
      fan: 'bg-orange-100 text-orange-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-bold rounded capitalize ${colors[role] || 'bg-slate-100 text-slate-700'}`}>
        {role || 'No role'}
      </span>
    );
  };

  // Multi-select functions
  const toggleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.user_id)));
    }
  };

  const isAllSelected = users.length > 0 && selectedUsers.size === users.length;
  const isSomeSelected = selectedUsers.size > 0 && selectedUsers.size < users.length;

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const deletePromises = Array.from(selectedUsers).map(userId =>
        api.delete(`/admin/users/${userId}`)
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedUsers.size} user(s) deleted`);
      setSelectedUsers(new Set());
      setShowBulkDeleteConfirm(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete some users');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Clear selection when page/filters change
  useEffect(() => {
    setSelectedUsers(new Set());
  }, [page, roleFilter, statusFilter, search]);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">
            User Management
          </h1>
          <p className="text-slate-600 mt-1">Manage all platform users</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="bg-slate-900">
          <UserPlus className="w-4 h-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border-2 border-slate-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="h-10"
              data-testid="user-search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[140px] h-10">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="coach">Coach</SelectItem>
              <SelectItem value="staff">Staff</SelectItem>
              <SelectItem value="player">Player</SelectItem>
              <SelectItem value="fan">Fan</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[140px] h-10">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="deleted">Deleted</SelectItem>
              <SelectItem value="test">Test Users</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" className="h-10">
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
        </form>
      </div>

      {/* Bulk Action Bar */}
      {selectedUsers.size > 0 && (
        <div className="bg-slate-800 text-white rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5" />
            <span className="font-medium">{selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedUsers(new Set())}
              className="text-white hover:bg-slate-700"
            >
              Clear Selection
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowBulkDeleteConfirm(true)}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={toggleSelectAll}
                    className="border-slate-400"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">User</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Email</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Role</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Status</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Created</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">Loading...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">No users found</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id} className={`hover:bg-slate-50 ${selectedUsers.has(user.user_id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selectedUsers.has(user.user_id)}
                        onCheckedChange={() => toggleSelectUser(user.user_id)}
                        className="border-slate-400"
                        data-testid={`select-user-${user.user_id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-600">
                          {user.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-900">{user.name}</p>
                            {user.is_test_user && (
                              <span className="px-1.5 py-0.5 text-xs font-bold bg-red-100 text-red-700 rounded" title="Test Account">
                                TEST USER
                              </span>
                            )}
                            {user.role === 'admin' && (
                              <span className="px-1.5 py-0.5 text-xs font-bold bg-indigo-100 text-indigo-700 rounded">
                                ADMIN
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{user.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{user.email}</td>
                    <td className="px-4 py-3">
                      {getRoleBadge(user.role)}
                      {user.role === 'fan' && user.seat_count !== undefined && (
                        <span className="ml-1.5 px-1.5 py-0.5 text-xs font-bold bg-teal-100 text-teal-700 rounded" data-testid={`seat-count-${user.user_id}`}>
                          {user.seat_count} seat{user.seat_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(user)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end space-x-2">
                        {/* Proxy Login - only for non-admin, active users */}
                        {user.role !== 'admin' && !user.deleted_at && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            title="Proxy Login (View as User)"
                            onClick={() => setImpersonateConfirm(user)}
                            data-testid={`impersonate-${user.user_id}`}
                          >
                            <LogIn className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}
                        <Link to={`/admin/users/${user.user_id}`}>
                          <Button variant="ghost" size="sm" title="View Details">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button variant="ghost" size="sm" title="Edit" onClick={() => setEditUser(user)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          title="Generate Temp Password"
                          onClick={() => handleGenerateTempPassword(user)}
                          data-testid={`temp-password-${user.user_id}`}
                        >
                          <Key className="w-4 h-4 text-amber-600" />
                        </Button>
                        {/* Status Toggle - replaces Suspend */}
                        {!user.deleted_at && user.role !== 'admin' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            title={user.status === 'inactive' || user.is_suspended ? "Set Active" : "Set Inactive"}
                            onClick={() => setStatusModal(user)}
                            data-testid={`status-toggle-${user.user_id}`}
                          >
                            <UserCheck className={`w-4 h-4 ${user.status === 'inactive' || user.is_suspended ? 'text-slate-400' : 'text-green-600'}`} />
                          </Button>
                        )}
                        {user.deleted_at ? (
                          <Button variant="ghost" size="sm" title="Restore" onClick={() => handleRestore(user.user_id)}>
                            <RotateCcw className="w-4 h-4 text-green-600" />
                          </Button>
                        ) : (
                          <>
                            {/* Delete - only enabled for test users */}
                            {user.is_test_user ? (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                title="Hard Delete Test Account"
                                onClick={() => setHardDeleteConfirm(user)}
                                data-testid={`hard-delete-${user.user_id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            ) : (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                title="Hard delete available only for Test User accounts"
                                disabled
                                className="cursor-not-allowed"
                              >
                                <Trash2 className="w-4 h-4 text-slate-300" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total} users
          </p>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * limit >= total}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold uppercase">Edit User</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Name</label>
                <Input
                  value={editUser.name || ''}
                  onChange={(e) => setEditUser({...editUser, name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Email</label>
                <Input
                  value={editUser.email || ''}
                  onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Role</label>
                <Select value={editUser.role || ''} onValueChange={(v) => setEditUser({...editUser, role: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="player">Player</SelectItem>
                    <SelectItem value="fan">Fan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex space-x-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setEditUser(null)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-slate-900" onClick={handleUpdateUser}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold uppercase text-red-600">Archive User</DialogTitle>
          </DialogHeader>
          {deleteConfirm && (
            <div className="py-4">
              <p className="text-slate-600 mb-4">
                Are you sure you want to archive <strong>{deleteConfirm.name}</strong> ({deleteConfirm.email})?
                This action will soft-delete the user. They can be restored later.
              </p>
              <div className="flex space-x-3">
                <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700" 
                  onClick={() => handleDelete(deleteConfirm.user_id)}
                >
                  Archive User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Status Modal */}
      <Dialog open={!!statusModal} onOpenChange={() => setStatusModal(null)}>
        <DialogContent data-testid="status-modal">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase">Update Customer Status</DialogTitle>
          </DialogHeader>
          {statusModal && (
            <div className="py-4">
              <p className="text-slate-600 mb-4">
                Set status for <strong>{statusModal.name}</strong> ({statusModal.email})
              </p>
              <div className="space-y-3 mb-6">
                <Button
                  variant={statusModal.status !== 'inactive' && !statusModal.is_suspended ? 'default' : 'outline'}
                  className={`w-full justify-start ${statusModal.status !== 'inactive' && !statusModal.is_suspended ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  onClick={() => handleUpdateStatus(statusModal.user_id, 'active')}
                >
                  <UserCheck className="w-4 h-4 mr-2" />
                  Active
                  <span className="ml-auto text-xs opacity-70">User can access the app</span>
                </Button>
                <Button
                  variant={statusModal.status === 'inactive' || statusModal.is_suspended ? 'default' : 'outline'}
                  className={`w-full justify-start ${statusModal.status === 'inactive' || statusModal.is_suspended ? 'bg-slate-600 hover:bg-slate-700' : ''}`}
                  onClick={() => handleUpdateStatus(statusModal.user_id, 'inactive')}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Inactive
                  <span className="ml-auto text-xs opacity-70">User is blocked from access</span>
                </Button>
              </div>
              <Button variant="outline" className="w-full" onClick={() => setStatusModal(null)}>
                Cancel
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hard Delete Confirmation */}
      <Dialog open={!!hardDeleteConfirm} onOpenChange={() => { setHardDeleteConfirm(null); setHardDeleteEmail(''); }}>
        <DialogContent data-testid="hard-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Permanently Delete Test Account
            </DialogTitle>
          </DialogHeader>
          {hardDeleteConfirm && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 space-y-2 text-sm text-red-800">
                <p className="font-semibold">You are permanently deleting this test account. This cannot be undone.</p>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>All teams associated with this account will be deleted.</li>
                  <li>All games for those teams will be deleted.</li>
                  <li>Claimed player accounts will remain active until permanently deleted.</li>
                </ul>
              </div>
              <p className="text-slate-600 mb-4">
                To confirm, type the user's email: <strong className="font-mono">{hardDeleteConfirm.email}</strong>
              </p>
              <Input
                value={hardDeleteEmail}
                onChange={(e) => setHardDeleteEmail(e.target.value)}
                placeholder="Type email to confirm"
                className="mb-4"
                data-testid="hard-delete-email-input"
              />
              <div className="flex space-x-3">
                <Button variant="outline" className="flex-1" onClick={() => { setHardDeleteConfirm(null); setHardDeleteEmail(''); }}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700" 
                  onClick={() => handleHardDelete(hardDeleteConfirm)}
                  disabled={hardDeleteEmail !== hardDeleteConfirm.email}
                  data-testid="confirm-hard-delete-btn"
                >
                  Permanently Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold uppercase">Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Full Name *</label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Email *</label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Password (optional)</label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                placeholder="Leave blank to auto-generate"
              />
            </div>
            <div>
              <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Role</label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="player">Player</SelectItem>
                  <SelectItem value="fan">Fan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex space-x-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-slate-900" onClick={handleCreateUser}>
                Create User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Impersonate Confirmation Dialog */}
      <Dialog open={!!impersonateConfirm} onOpenChange={() => setImpersonateConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold uppercase text-blue-600">Proxy Login</DialogTitle>
          </DialogHeader>
          {impersonateConfirm && (
            <div className="py-4">
              <p className="text-slate-600 mb-2">
                You are about to view the app as <strong>{impersonateConfirm.name || 'this user'}</strong> ({impersonateConfirm.email}).
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Read-only mode:</strong> You will see the app exactly as this user sees it. A banner will indicate you are in proxy mode. Click "Exit Proxy" to return to admin view.
                </p>
              </div>
              <div className="flex space-x-3">
                <Button variant="outline" className="flex-1" onClick={() => setImpersonateConfirm(null)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700" 
                  onClick={() => handleImpersonate(impersonateConfirm)}
                  data-testid="confirm-impersonate"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  View as User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Temp Password Modal */}
      <TempPasswordModal
        open={tempPasswordModal.open}
        onOpenChange={(open) => setTempPasswordModal({ ...tempPasswordModal, open })}
        tempPassword={tempPasswordModal.password}
        userName={tempPasswordModal.user?.name}
        userEmail={tempPasswordModal.user?.email}
      />

      {/* Bulk Delete Confirmation */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold uppercase text-red-600">
              Delete {selectedUsers.size} User{selectedUsers.size > 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              This action will soft-delete the selected users. They can be restored later.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 font-medium">
                You are about to delete {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''}.
              </p>
              <p className="text-red-600 text-sm mt-1">
                This will archive the accounts. Users can be restored from the "Deleted" filter.
              </p>
            </div>
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => setShowBulkDeleteConfirm(false)}
                disabled={bulkDeleting}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700" 
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? 'Deleting...' : `Delete ${selectedUsers.size} User${selectedUsers.size > 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
